import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';

const STATUS_OPTIONS = [
  'New', 'Elizabeth Called', 'Hot Lead', 'Gideon Called',
  'Closed Won', 'Closed Lost', 'Not Interested'
];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activity, setActivity] = useState([]);
  const [notes, setNotes] = useState('');
  const [calendlyLink, setCalendlyLink] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const { lead, activity } = await api.getLead(id);
    setLead(lead);
    setActivity(activity);
    setNotes(lead.notes || '');
  }, [id]);

  useEffect(() => {
    load();
    api.config().then(c => setCalendlyLink(c.calendlyLink || '')).catch(() => {});
  }, [load]);

  if (!lead) return <div className="crm-shell"><div className="empty">Loading...</div></div>;

  async function changeStatus(status) {
    const updated = await api.patchLead(id, { status });
    setLead(updated);
    load();
  }

  async function toggleHot() {
    const turningOn = !lead.hot_lead;
    const body = { hot_lead: turningOn };
    if (turningOn && !['Hot Lead','Gideon Called','Closed Won','Closed Lost'].includes(lead.status)) {
      body.status = 'Hot Lead';
    }
    const updated = await api.patchLead(id, body);
    setLead(updated);
    load();
  }

  async function saveNotes() {
    setSavingNotes(true);
    try {
      const updated = await api.patchLead(id, { notes });
      setLead(updated);
    } finally {
      setSavingNotes(false);
    }
  }

  async function logCall() {
    const updated = await api.recordCallAttempt(id);
    setLead(updated);
    load();
  }

  async function deleteLead() {
    if (!window.confirm(`Delete lead "${lead.name}"? This cannot be undone.`)) return;
    await api.deleteLead(id);
    navigate('/crm');
  }

  function copyCalendly() {
    if (!calendlyLink) return;
    navigator.clipboard.writeText(calendlyLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const tgHandle = String(lead.telegram_handle || '').replace(/^@+/, '').replace(/\s+/g, '').trim();
  const tgWebUrl = `https://t.me/${encodeURIComponent(tgHandle)}`;
  const tgAppUrl = `tg://resolve?domain=${encodeURIComponent(tgHandle)}`;

  return (
    <div className="crm-shell detail">
      <header className="crm-header">
        <div className="crm-brand">
          <Link to="/crm" className="back-link">← Back to leads</Link>
        </div>
        <button className="btn-ghost" onClick={() => navigate('/crm')}>Close</button>
      </header>

      <div className="detail-grid">
        <div className="detail-main">
          <div className="detail-name-row">
            <h1>{lead.name}</h1>
            <button className={`hot-toggle ${lead.hot_lead ? 'on' : ''}`} onClick={toggleHot}>
              {lead.hot_lead ? '🔥 Hot Lead' : 'Mark hot'}
            </button>
          </div>

          <div className="info-grid">
            <Field label="Telegram"><a href={tgAppUrl} className="link">{lead.telegram_handle}</a></Field>
            <Field label="Phone">{lead.phone}</Field>
            <Field label="Email"><a href={`mailto:${lead.email}`} className="link">{lead.email}</a></Field>
            <Field label="Country">{lead.country}</Field>
            <Field label="Crypto experience">{lead.crypto_experience}</Field>
            <Field label="Savings to invest">{lead.savings_range}</Field>
            <Field label="VIP status">{lead.vip_status}</Field>
            <Field label="Has Telegram">{lead.has_telegram ? 'Yes' : <span style={{ color: '#ff9a3f' }}>No (warned)</span>}</Field>
            <Field label="Submitted">{new Date(lead.created_at).toLocaleString()}</Field>
            <Field label="Call attempts">{lead.call_attempts}</Field>
          </div>

          <div className="card">
            <h3>Notes</h3>
            <textarea
              className="notes-area"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="What did they say on the call? Any context for the next person?"
            />
            <button className="btn-primary small" onClick={saveNotes} disabled={savingNotes}>
              {savingNotes ? 'Saving...' : 'Save notes'}
            </button>
          </div>

          <div className="card">
            <h3>Activity</h3>
            <ul className="activity-list">
              {activity.map(a => (
                <li key={a.id}>
                  <span className="muted small">{new Date(a.created_at).toLocaleString()}</span>
                  <span> — </span>
                  <span>{a.detail}</span>
                  {a.actor && a.actor !== 'system' && <span className="muted small"> ({a.actor})</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="detail-side">
          <div className="card">
            <h3>Status</h3>
            <select
              className="text-input select full"
              value={lead.status}
              onChange={e => changeStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="card">
            <h3>Actions</h3>
            <a className="btn-primary block" href={tgUrl} target="_blank" rel="noreferrer">Open Telegram</a>
            <button className="btn-ghost block" onClick={logCall}>Log call attempt (+1)</button>
            {calendlyLink ? (
              <>
                <a className="btn-ghost block" href={calendlyLink} target="_blank" rel="noreferrer">Open Calendly</a>
                <button className="btn-ghost block" onClick={copyCalendly}>
                  {copied ? 'Copied!' : 'Copy Calendly link'}
                </button>
              </>
            ) : (
              <p className="muted small">Add CALENDLY_LINK to env to enable booking links.</p>
            )}
          </div>

          {lead.status === 'Closed Won' && (
            <div className="card success-card">
              <strong>Closed won 🎉</strong>
              <p className="muted small">Tagged source: {lead.source_tag}. The signup bot will pick them up when they join VIP.</p>
            </div>
          )}

          <div className="card">
            <h3>Danger zone</h3>
            <button className="btn-danger block" onClick={deleteLead}>Delete lead</button>
            <p className="muted small" style={{ marginTop: 8 }}>Permanent. Use for spam or test entries.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div className="field-value">{children}</div>
    </div>
  );
}
